// ============================================
// CONTRATO DA PLATAFORMA — assinatura no login do cliente
//
// Quando a Klinvia envia um contrato pelo painel central, ele aparece aqui para
// o administrador da clínica: lê o PDF, confirma os dados e assina com as
// mesmas formas do resto do sistema (traço, nome digitado ou certificado
// ICP-Brasil). "Lembrar mais tarde" vale só para a sessão: no próximo login o
// contrato volta. Contratos obrigatórios (ou com prazo vencido) não têm adiar.
// ============================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CheckCircleRounded as CheckCircleIcon,
  Draw as DrawIcon,
  Keyboard as KeyboardIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
  HistoryEdu as ContractIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';
import SignaturePad, { type SignaturePadHandle } from '../signature/SignaturePad';
import { base64PdfToBlob, downloadBase64Pdf, fileToBase64 } from '../../utils/pdfBase64';
import { readSnoozedContracts, snoozeContract } from '../../utils/platformContractSnooze';
import PlatformActivationPayment, { type ActivationState } from './PlatformActivationPayment';
import { isKlinviaNativeApp } from '../../utils/nativeApp';

const TEAL = '#0D9488';
const NAVY = '#0F172A';
const AMBER = '#B45309';

const POLL_MS = 10 * 60 * 1000;

type SignatureMethod = 'handwritten' | 'typed' | 'icp';

interface PendingContract {
  id: string;
  title: string;
  version: string | null;
  message: string | null;
  fileName: string | null;
  pageCount: number | null;
  mustSignNow: boolean;
  signDeadline: string | null;
  sentAt: string;
  canSign: boolean;
}

function formatCpfInput(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
}

function formatDate(ymd: string | null): string {
  if (!ymd) return '';
  return new Date(`${String(ymd).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR');
}

function apiError(e: unknown, fallback: string): string {
  return axios.isAxiosError(e) && e.response?.data?.error ? String(e.response.data.error) : fallback;
}

const SIGNER_ROLES = new Set(['admin_master', 'admin']);

const PlatformContractGate: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const nativeApp = isKlinviaNativeApp();

  const [pending, setPending] = useState<PendingContract[]>([]);
  const [snoozed, setSnoozed] = useState<string[]>(() => readSnoozedContracts());
  const [step, setStep] = useState<'read' | 'sign' | 'payment' | 'done'>('read');
  /** Contrato que exige pagamento: assinar não basta, o acesso abre na confirmação. */
  const [activation, setActivation] = useState<ActivationState | null>(null);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const viewedRef = useRef<Set<string>>(new Set());

  const [accepted, setAccepted] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerCpf, setSignerCpf] = useState('');
  const [signerRoleTitle, setSignerRoleTitle] = useState('');
  const [method, setMethod] = useState<SignatureMethod>('handwritten');
  const [hasDrawing, setHasDrawing] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [certificatePassphrase, setCertificatePassphrase] = useState('');
  const [password, setPassword] = useState('');
  const padRef = useRef<SignaturePadHandle | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [signed, setSigned] = useState<{ id: string; title: string } | null>(null);

  const eligible = Boolean(user && SIGNER_ROLES.has(user.role));

  const load = useCallback(async () => {
    if (!eligible) return;
    try {
      const res = await axios.get<{ success: boolean; data: PendingContract[] }>(
        getApiUrl('/platform-plan/contracts/pending'),
      );
      setPending(res.data.data || []);
    } catch {
      // Contrato nunca pode travar o uso do sistema por falha de rede.
    }
  }, [eligible]);

  useEffect(() => {
    void load();
    if (!eligible) return undefined;
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [load, eligible]);

  const current = useMemo(
    () => pending.find((c) => (c.mustSignNow && c.canSign) || !snoozed.includes(c.id)) ?? null,
    [pending, snoozed],
  );

  /** Contrato cuja leitura está ativa; null na tela de "assinado" evita recarregar o PDF no meio do fluxo. */
  const activeContractId = current && step !== 'done' ? current.id : null;
  const defaultSignerName = user?.full_name || '';

  // Troca de contrato (ou volta da tela de concluído): zera o formulário e carrega o PDF.
  useEffect(() => {
    if (!activeContractId) return undefined;

    setStep('read');
    setAccepted(false);
    setActionError(null);
    setSignerName(defaultSignerName);
    setTypedName(defaultSignerName);
    setMethod('handwritten');
    setHasDrawing(false);
    setCertificateFile(null);
    setCertificatePassphrase('');
    setPassword('');

    let objectUrl: string | null = null;
    let cancelled = false;
    setPdfUrl(null);
    setPdfError(null);
    setPdfLoading(true);
    axios
      .get<{ success: boolean; data: { pdfBase64: string } }>(
        getApiUrl(`/platform-plan/contracts/${activeContractId}/file`),
        { params: { kind: 'original' } },
      )
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(base64PdfToBlob(res.data.data.pdfBase64));
        setPdfUrl(objectUrl);
        if (!viewedRef.current.has(activeContractId)) {
          viewedRef.current.add(activeContractId);
          void axios.post(getApiUrl(`/platform-plan/contracts/${activeContractId}/view`)).catch(() => undefined);
        }
      })
      .catch((e) => !cancelled && setPdfError(apiError(e, 'Não foi possível abrir o contrato.')))
      .finally(() => !cancelled && setPdfLoading(false));

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [activeContractId, defaultSignerName]);

  const remindLater = () => {
    if (!current) return;
    snoozeContract(current.id);
    setSnoozed(readSnoozedContracts());
  };

  const cpfDigits = signerCpf.replace(/\D/g, '');
  const methodReady =
    method === 'handwritten'
      ? hasDrawing
      : method === 'typed'
        ? typedName.trim().length >= 3
        : Boolean(certificateFile && certificatePassphrase);
  const canSubmit =
    signerName.trim().length >= 3 && cpfDigits.length === 11 && password.length > 0 && methodReady && !submitting;

  const submit = async () => {
    if (!current) return;
    setActionError(null);

    const payload: Record<string, unknown> = {
      method,
      accepted_terms: accepted,
      password,
      signer_name: signerName.trim(),
      signer_cpf: cpfDigits,
      signer_role_title: signerRoleTitle.trim() || null,
    };
    if (method === 'handwritten') {
      const dataUrl = padRef.current?.toDataUrl();
      if (!dataUrl) {
        setActionError('Desenhe a assinatura antes de confirmar.');
        return;
      }
      payload.signature_image_base64 = dataUrl;
    } else if (method === 'typed') {
      payload.typed_name = typedName.trim();
    } else if (certificateFile) {
      payload.certificate_base64 = await fileToBase64(certificateFile);
      payload.certificate_passphrase = certificatePassphrase;
    }

    setSubmitting(true);
    try {
      const res = await axios.post<{ success: boolean; data?: { activation?: ActivationState | null } }>(
        getApiUrl(`/platform-plan/contracts/${current.id}/sign`),
        payload,
        { timeout: 90_000 },
      );
      const signedActivation = res.data?.data?.activation ?? null;
      setSigned({ id: current.id, title: current.title });
      setPending((list) => list.filter((c) => c.id !== current.id));

      if (signedActivation?.required && !signedActivation.paid) {
        setActivation(signedActivation);
        setStep('payment');
      } else {
        setStep('done');
      }
    } catch (e) {
      setActionError(apiError(e, 'Não foi possível concluir a assinatura. Tente novamente.'));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadSigned = async () => {
    if (!signed) return;
    try {
      const res = await axios.get<{ success: boolean; data: { pdfBase64: string; fileName: string } }>(
        getApiUrl(`/platform-plan/contracts/${signed.id}/file`),
        { params: { kind: 'signed' } },
      );
      downloadBase64Pdf(res.data.data.pdfBase64, res.data.data.fileName);
    } catch (e) {
      setActionError(apiError(e, 'Não foi possível baixar o contrato assinado.'));
    }
  };

  const finish = () => {
    setSigned(null);
    setStep('read');
  };

  if (!eligible) return null;
  const open = step === 'done' || step === 'payment' ? Boolean(signed) : Boolean(current);
  if (!open) return null;

  return (
    <Dialog
      open
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(15, 23, 42, 0.82)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          '@media (prefers-reduced-motion: reduce)': {
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            backgroundColor: 'rgba(15, 23, 42, 0.92)',
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          height: fullScreen ? '100%' : '92vh',
          boxShadow: '0 32px 80px rgba(2, 6, 23, 0.45)',
        },
      }}
    >
      {step === 'payment' && (
        <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="overline" sx={{ color: TEAL, fontWeight: 700, letterSpacing: '0.08em' }}>
            Contrato assinado · último passo
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: 18, md: 21 }, color: NAVY, letterSpacing: '-0.01em' }}>
            {nativeApp ? 'Liberação do acesso' : 'Pagamento do plano'}
          </Typography>
          {!nativeApp && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Esta é a fatura do seu plano — não há cobrança extra. O sistema é liberado assim que o
              pagamento for confirmado; com PIX, isso leva segundos.
            </Typography>
          )}
        </Box>
      )}

      {step !== 'done' && step !== 'payment' && current && (
        <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: 'rgba(13,148,136,0.12)',
                color: TEAL,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <ContractIcon />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="overline" sx={{ color: TEAL, fontWeight: 700, letterSpacing: '0.08em', lineHeight: 1.4 }}>
                Contrato da plataforma Klinvia
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: { xs: 18, md: 21 }, color: NAVY, letterSpacing: '-0.01em' }}>
                {current.title}
                {current.version ? ` · versão ${current.version}` : ''}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                {current.mustSignNow ? (
                  <Chip size="small" label="Assinatura obrigatória para continuar" sx={{ bgcolor: 'rgba(180,83,9,0.12)', color: AMBER, fontWeight: 700 }} />
                ) : current.signDeadline ? (
                  <Chip size="small" label={`Assine até ${formatDate(current.signDeadline)}`} sx={{ fontWeight: 600 }} />
                ) : null}
                <Chip
                  size="small"
                  variant="outlined"
                  label={step === 'read' ? '1 de 2 · Leitura' : '2 de 2 · Assinatura'}
                  sx={{ fontWeight: 600 }}
                />
              </Stack>
            </Box>
          </Stack>
          {current.message && step === 'read' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>
              {current.message}
            </Typography>
          )}
        </Box>
      )}

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {step === 'read' && current && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: '#F1F5F9' }}>
            {pdfLoading && (
              <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
                <CircularProgress />
              </Box>
            )}
            {pdfError && (
              <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {pdfError}
                </Alert>
              </Box>
            )}
            {pdfUrl && (
              <>
                <Box
                  component="iframe"
                  src={pdfUrl}
                  title={current.title}
                  sx={{ flex: 1, width: '100%', border: 0, minHeight: 320 }}
                />
                <Stack direction="row" spacing={1} sx={{ px: 2, py: 1, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
                  <Button size="small" startIcon={<OpenInNewIcon />} href={pdfUrl} target="_blank" rel="noopener" sx={{ textTransform: 'none' }}>
                    Abrir em nova aba
                  </Button>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    href={pdfUrl}
                    download={current.fileName || 'contrato.pdf'}
                    sx={{ textTransform: 'none' }}
                  >
                    Baixar
                  </Button>
                </Stack>
              </>
            )}
          </Box>
        )}

        {step === 'sign' && current && (
          <Box sx={{ overflowY: 'auto', px: { xs: 2, md: 3 }, py: 2.5 }}>
            <Stack spacing={2.5} sx={{ maxWidth: 640, mx: 'auto' }}>
              <Box>
                <Typography sx={{ fontWeight: 700, color: NAVY, mb: 1.25 }}>Quem assina pela clínica</Typography>
                <Stack spacing={1.5}>
                  <TextField label="Nome completo" value={signerName} onChange={(e) => setSignerName(e.target.value)} fullWidth />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      label="CPF"
                      value={signerCpf}
                      onChange={(e) => setSignerCpf(formatCpfInput(e.target.value))}
                      inputProps={{ inputMode: 'numeric' }}
                      fullWidth
                    />
                    <TextField
                      label="Cargo (opcional)"
                      value={signerRoleTitle}
                      onChange={(e) => setSignerRoleTitle(e.target.value)}
                      placeholder="Sócio-administrador"
                      fullWidth
                    />
                  </Stack>
                </Stack>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 700, color: NAVY, mb: 1.25 }}>Assinatura</Typography>
                <ToggleButtonGroup
                  value={method}
                  exclusive
                  fullWidth
                  onChange={(_e, value) => value && setMethod(value as SignatureMethod)}
                  sx={{
                    mb: 1.5,
                    '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, fontSize: 14, py: 1, borderRadius: 2 },
                    '& .Mui-selected': { bgcolor: `${TEAL} !important`, color: '#fff !important' },
                  }}
                >
                  <ToggleButton value="handwritten">
                    <DrawIcon fontSize="small" sx={{ mr: 0.75 }} />
                    Manuscrita
                  </ToggleButton>
                  <ToggleButton value="typed">
                    <KeyboardIcon fontSize="small" sx={{ mr: 0.75 }} />
                    Digitada
                  </ToggleButton>
                  <ToggleButton value="icp">Certificado</ToggleButton>
                </ToggleButtonGroup>

                {method === 'handwritten' && <SignaturePad ref={padRef} onChange={setHasDrawing} height={200} />}

                {method === 'typed' && (
                  <Stack spacing={1.5}>
                    <TextField value={typedName} onChange={(e) => setTypedName(e.target.value)} fullWidth placeholder="Nome completo" />
                    <Box sx={{ bgcolor: '#F1F5F9', borderRadius: 2, py: 3, px: 2, textAlign: 'center', minHeight: 90, display: 'grid', placeItems: 'center' }}>
                      <Typography sx={{ fontFamily: "'Segoe Script', 'Brush Script MT', cursive", fontSize: 30, color: NAVY, wordBreak: 'break-word' }}>
                        {typedName || '—'}
                      </Typography>
                    </Box>
                  </Stack>
                )}

                {method === 'icp' && (
                  <Stack spacing={1.5}>
                    <Typography variant="body2" color="text.secondary">
                      Certificado ICP-Brasil A1 (.pfx ou .p12). O contrato recebe selo PAdES verificável no validador do
                      ITI; o arquivo do certificado é usado só nesta assinatura e não fica guardado.
                    </Typography>
                    <Button component="label" variant="outlined" sx={{ textTransform: 'none', py: 1.25, borderRadius: 2 }}>
                      {certificateFile ? certificateFile.name : 'Selecionar certificado'}
                      <input type="file" hidden accept=".pfx,.p12" onChange={(e) => setCertificateFile(e.target.files?.[0] || null)} />
                    </Button>
                    <TextField
                      type="password"
                      label="Senha do certificado"
                      value={certificatePassphrase}
                      onChange={(e) => setCertificatePassphrase(e.target.value)}
                      fullWidth
                    />
                  </Stack>
                )}
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 700, color: NAVY, mb: 0.5 }}>Confirme sua identidade</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                  Digite a senha do seu login ({user?.email}). Ela comprova que é você quem está assinando.
                </Typography>
                <TextField
                  type="password"
                  label="Sua senha do sistema"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  fullWidth
                />
              </Box>

              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ color: 'text.secondary' }}>
                <VerifiedUserIcon sx={{ fontSize: 18, mt: 0.25, color: TEAL }} />
                <Typography variant="caption">
                  Registramos data, hora, IP e dispositivo desta assinatura. O PDF assinado recebe uma página de
                  certificado com essas evidências e o código de validação, e fica guardado com a Klinvia.
                </Typography>
              </Stack>
            </Stack>
          </Box>
        )}

        {step === 'payment' && (
          <Box sx={{ overflowY: 'auto', px: { xs: 2, md: 3 }, py: 2.5 }}>
            <Box sx={{ maxWidth: 520, mx: 'auto' }}>
              <PlatformActivationPayment
                initial={activation}
                onReleased={() => {
                  // Recarrega para o gate de cobrança e o menu voltarem liberados.
                  window.location.reload();
                }}
              />
            </Box>
          </Box>
        )}

        {step === 'done' && signed && (
          <Stack spacing={2.5} alignItems="center" justifyContent="center" sx={{ flex: 1, p: 4, textAlign: 'center' }}>
            <Box sx={{ width: 76, height: 76, borderRadius: '50%', bgcolor: 'rgba(13,148,136,0.12)', display: 'grid', placeItems: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 46, color: TEAL }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 22, color: NAVY, letterSpacing: '-0.02em' }}>
              Contrato assinado
            </Typography>
            <Typography sx={{ color: 'text.secondary', maxWidth: 460, lineHeight: 1.6 }}>
              {signed.title} foi assinado com sucesso. Uma cópia fica disponível em Mensalidade da plataforma e outra
              guardada com a Klinvia.
            </Typography>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => void downloadSigned()} sx={{ textTransform: 'none', borderRadius: 2 }}>
              Baixar contrato assinado
            </Button>
          </Stack>
        )}
      </DialogContent>

      {actionError && (
        <Box sx={{ px: { xs: 2, md: 3 }, pb: 1 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {actionError}
          </Alert>
        </Box>
      )}

      {step === 'read' && current && !current.canSign && (
        <Box sx={{ px: { xs: 2, md: 3 }, pb: 1 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Esta sessão foi aberta via "acessar como" e não pode assinar em nome da clínica.
          </Alert>
        </Box>
      )}

      {step === 'payment' && (
        <Box sx={{ px: { xs: 2, md: 3 }, py: 1.75, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Uma cópia do contrato assinado fica em Mensalidade da plataforma. Precisa de outra forma de
            pagamento? Fale com o suporte da Klinvia.
          </Typography>
        </Box>
      )}

      <DialogActions
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.75,
          borderTop: '1px solid',
          borderColor: 'divider',
          flexWrap: 'wrap',
          gap: 1,
          display: step === 'payment' ? 'none' : undefined,
        }}
      >
        {step === 'read' && current && (
          <>
            <FormControlLabel
              sx={{ mr: 'auto' }}
              control={<Checkbox checked={accepted} onChange={(e) => setAccepted(e.target.checked)} disabled={!pdfUrl} />}
              label={<Typography variant="body2">Li e concordo com os termos deste contrato</Typography>}
            />
            {(!current.mustSignNow || !current.canSign) && (
              <Button onClick={remindLater} sx={{ textTransform: 'none' }}>
                Lembrar mais tarde
              </Button>
            )}
            <Button
              variant="contained"
              disabled={!accepted || !current.canSign}
              onClick={() => setStep('sign')}
              sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#0F766E' }, fontWeight: 700, textTransform: 'none', borderRadius: 2 }}
            >
              Continuar para assinatura
            </Button>
          </>
        )}
        {step === 'sign' && (
          <>
            <Button onClick={() => setStep('read')} disabled={submitting} sx={{ textTransform: 'none', mr: 'auto' }}>
              Voltar ao contrato
            </Button>
            <Button
              variant="contained"
              disabled={!canSubmit}
              onClick={() => void submit()}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#0F766E' }, fontWeight: 700, textTransform: 'none', borderRadius: 2 }}
            >
              {submitting ? 'Assinando…' : 'Assinar contrato'}
            </Button>
          </>
        )}
        {step === 'done' && (
          <Button
            variant="contained"
            onClick={finish}
            sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#0F766E' }, fontWeight: 700, textTransform: 'none', borderRadius: 2 }}
          >
            {pending.length ? 'Próximo contrato' : 'Continuar usando o sistema'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PlatformContractGate;
